import {
  contextMenuTemplate,
  globalMenuTemplate,
  greetingTemplate,
  placeholderTemplate,
  globalMenuPlaceholderSelector,
  contextMenuPlaceholderSelector,
  greetingPlaceholderSelector
} from './menu-templates';
import styles from './menu.css';
import {
  emptyElement,
  getFirstElementChild,
  getLastElementChild
} from '../../utils/utils';
import { isCCApp } from '../../api/api.js';
import { clearCookie } from '../../fetch-api/lib/auth-token';

let initialMenuId = 0;

class Menu {
  constructor(menuPlaceholder) {
    if (document.querySelector(menuPlaceholder) !== null) {
      initialMenuId += 1;
      this.id = `menu${initialMenuId - 1}`;
      this.menuContainerNode = document.querySelector(menuPlaceholder);
      this.id = `menu${initialMenuId - 1}`;
    } else {
      console.error(
        `Selector ${menuPlaceholder} not present in document, please pass a valid selector`,
      );
    }
  }

  defaultProps = {
    globalMenu: [],
    isGlobalMenuOpen: false,
    contextMenu: [],
    greeting: {},
    isEclientLink: false
  };

  set isEclientLink(x) {
    this.props.isEclientLink = x;
  }

  set globalMenu(globalMenuData) {
    this.props.globalMenu = globalMenuData;
    const globalMenuFragment = document
      .createRange()
      .createContextualFragment(
        globalMenuTemplate(
          globalMenuData,
          `${this.id}_global`,
          this.props.isEclientLink,
        ),
      );
    const globalMenuFirstElementChild =
      getFirstElementChild(globalMenuFragment);
    this.globalMenuNode.parentNode.replaceChild(
      globalMenuFirstElementChild,
      this.globalMenuNode,
    );
    this.globalMenuNode = globalMenuFirstElementChild;
    let controlDomNodes =
      this.globalMenuNode.querySelectorAll('[aria-controls]');
    controlDomNodes = Array.prototype.slice.call(controlDomNodes);
    controlDomNodes.forEach((controlDomNode) => {
      controlDomNode.addEventListener('click', () => {
        const globalMenuList = this.globalMenuNode.querySelector('ol');
        globalMenuList.classList.add(styles.animate);
        this.contextMenuNode.classList.add(styles.animate);
        this.isGlobalMenuOpen = !this.props.isGlobalMenuOpen;
      });
    });
    this.globalMenuNode.addEventListener(
      'keydown',
      this.handleGlobalMenuKeydown,
    );
  }

  set isGlobalMenuOpen(isOpen) {
    this.props.isGlobalMenuOpen = isOpen;
    const globalMenuList = this.globalMenuNode.querySelector('ol');
    const controlElement = globalMenuList.querySelector('[aria-controls]');
    if (controlElement !== null && controlElement.parentNode !== null) {
      const controlledElementIds = controlElement.getAttribute('aria-controls');
      const controlledElements = controlledElementIds
        .split(',')
        .map((controlledElementId) =>
          // eslint-disable-next-line
          this.globalMenuNode.querySelector(`#${controlledElementId}`), );
      if (isOpen) {
        controlledElements.forEach((controlledElement) => {
          if (controlledElement) {
            controlledElement.removeAttribute('disabled');
          }
        });
        requestAnimationFrame(() => {
          this.globalMenuNode.classList.add(styles.isOpen);
          controlElement.parentNode.style.setProperty(
            'transform',
            'translateY(0px)',
          );
          this.contextMenuNode.style.setProperty(
            'transform',
            'translateY(0px)',
          );
          controlledElements.forEach((controlledElement) => {
            if (controlledElement) {
              controlledElement.parentNode.style.setProperty('opacity', '1');
              controlledElement.parentNode.style.setProperty(
                'transform',
                'translateY(0px)',
              );
            }
          });
          this.setMenuContainerNodeOverflow();
        });
      } else {
        const firstElementChild = getFirstElementChild(globalMenuList);
        const firstElementTop = firstElementChild.offsetTop;
        const controlElementTop = controlElement.parentNode.offsetTop;
        const controlElementHeight = controlElement.parentNode.offsetHeight;
        const top = controlElementTop - firstElementTop;
        const lastElementChild = getLastElementChild(globalMenuList);
        const lastElementChildTop = lastElementChild.offsetTop;
        const lastElementChildHeight = lastElementChild.offsetHeight;
        const lastElementChildBottomPosition =
          lastElementChildTop + lastElementChildHeight;
        const controlElementBottomPosition =
          controlElementTop + controlElementHeight;
        const extraContextMenuMovement =
          lastElementChildBottomPosition - controlElementBottomPosition;
        const contextMenuMovement = top + extraContextMenuMovement;
        controlledElements.forEach((controlledElement) => {
          if (controlledElement) {
            controlledElement.setAttribute('disabled', 'disabled');
          }
        });
        requestAnimationFrame(() => {
          this.globalMenuNode.classList.remove(styles.isOpen);
          controlElement.parentNode.style.setProperty(
            'transform',
            `translateY(-${top}px)`,
          );
          this.contextMenuNode.style.setProperty(
            'transform',
            `translateY(-${contextMenuMovement}px)`,
          );
          controlledElements.forEach((controlledElement) => {
            if (controlledElement) {
              controlledElement.parentNode.style.setProperty('opacity', '0');
              controlledElement.parentNode.style.setProperty(
                'transform',
                `translateY(-${top}px)`,
              );
            }
          });
          this.setMenuContainerNodeOverflow();
        });
      }
    }
  }

  set contextMenu(contextMenuData) {
    // add ids for onClick functions
    // note that the ids don't need to be super unique. This simple count is sufficient
    let onClickId = 0;
    const addOnClickIds = (CMArray) =>
      CMArray.map(({ onClick, children, ...rest }) => {
        onClickId += 1;
        return {
          ...(onClick ? { onClick, onClickId } : {}),
          ...(children ? { children: addOnClickIds(children) } : {}),
          ...rest
        };
      });
    this.props.contextMenu = addOnClickIds(contextMenuData);
    // build an object with all the onclick functions
    const indexOnClickFunctions = (
      acc,
      { onClick, onClickId: idKey, children },
    ) => {
      const tempAcc = children
        ? children.reduce(indexOnClickFunctions, acc)
        : acc;
      return {
        ...tempAcc,
        ...(onClick && idKey ? { [idKey]: onClick } : {})
      };
    };
    const onClickFunctions = this.props.contextMenu.reduce(
      indexOnClickFunctions,
      {},
    );
    const previousAppliedStyles = this.contextMenuNode.getAttribute('style');
    const contextMenuFragment = document
      .createRange()
      .createContextualFragment(
        contextMenuTemplate(
          this.props.contextMenu,
          `${this.id}_context`,
          this.props.isEclientLink,
        ),
      );
    const contextMenuFirstElementChild =
      getFirstElementChild(contextMenuFragment);
    const expandedOlElements = contextMenuFirstElementChild.querySelectorAll(
      'ol[aria-expanded="true"]',
    );
    const secondExpandedOlElement = expandedOlElements[0];
    if (secondExpandedOlElement) {
      const anchorTags = secondExpandedOlElement.getElementsByTagName('a');
      for (let i = 0; i < anchorTags.length; i += 1) {
        anchorTags[i].setAttribute('tabIndex', '0');
      }
    }
    this.contextMenuNode.parentNode.replaceChild(
      contextMenuFirstElementChild,
      this.contextMenuNode,
    );
    this.contextMenuNode = contextMenuFirstElementChild;
    this.contextMenuNode.setAttribute('style', previousAppliedStyles);
    const onClickElms = Array.prototype.slice.call(
      this.contextMenuNode.querySelectorAll('[data-on-click-id]'),
    );
    onClickElms.forEach((node) => {
      node.addEventListener('click', onClickFunctions[node.dataset.onClickId]);
    });
    let controlDomNodes =
      this.contextMenuNode.querySelectorAll('[aria-controls]');
    controlDomNodes = Array.prototype.slice.call(controlDomNodes);
    controlDomNodes.forEach((controlDomNode) => {
      controlDomNode.addEventListener('click', (evt) => {
        this.contextMenuNode.classList.add(styles.animate);
        const { currentTarget } = evt;
        requestAnimationFrame(() => {
          this.handleContextMenuToggle({ currentTarget });
        });
      });
    });
    this.contextMenuNode.addEventListener('keydown', (evt) => {
      this.contextMenuNode.classList.add(styles.animate);
      const {
        currentTarget, keyCode, which, target
      } = evt;
      requestAnimationFrame(() => {
        this.handleContextMenuKeydown({
          currentTarget,
          keyCode,
          which,
          target
        });
      });
    });
  }

  set greeting(greetingData) {
    this.props.greeting = greetingData;
    const greetingFragment = document
      .createRange()
      .createContextualFragment(
        greetingTemplate(
          greetingData,
          this.props.isEclientLink,
          `${this.id}_greeting`,
        ),
      );
    const greetingFirstElementChild = getFirstElementChild(greetingFragment);
    this.globalMenuNode.parentNode.replaceChild(
      greetingFirstElementChild,
      this.greetingNode,
    );
    this.greetingNode = greetingFirstElementChild;
    const logoutButton = document.getElementById('logoutButton');
    logoutButton.addEventListener('click', () => {
      this.handleLogout(greetingData);
    });

    logoutButton.addEventListener('keypress', (event) => {
      if (event.key === 'Enter') {
        this.handleLogout(greetingData);
      }
    });
  }

  handleLogout = (greetingData) => {
    const { logoutLink = '' } = greetingData;
    if (isCCApp()) {
      clearCookie('_admin_auth_token');
      clearCookie('cc-admin-sso');
      const {
        location: { origin = '' }
      } = window;
      window.location.href = `${origin}/logout`;
    } else if (logoutLink) {
      clearCookie('_admin_auth_token');
      window.location.href = logoutLink;
    }
  };

  handleContextMenuToggle = ({ currentTarget }) => {
    const controlledElementIds = currentTarget.getAttribute('aria-controls');
    const controlledElements = controlledElementIds
      .split(',')
      .map((controlledElementId) =>
        // eslint-disable-next-line
        this.contextMenuNode.querySelector(`#${controlledElementId}`), );
    controlledElements.forEach((controlledElement) => {
      let temp = controlledElement.parentNode;
      while (temp !== this.contextMenuNode) {
        if (
          temp.style.getPropertyValue('height') !== '' &&
          temp.style.getPropertyValue('height') !== 'auto'
        ) {
          temp.style.setProperty('height', 'auto');
        }
        temp = temp.parentNode;
      }
      const anchorTags = controlledElement.getElementsByTagName('a');
      const ariaExpanded = controlledElement.getAttribute('aria-expanded');
      if (ariaExpanded === 'true') {
        for (let i = 0; i < anchorTags.length; i += 1) {
          anchorTags[i].setAttribute('tabIndex', '-1');
        }
        const height = controlledElement.scrollHeight;
        controlledElement.style.setProperty('height', `${height}px`);
        controlledElement.style.setProperty('opacity', '1');
        controlledElement.setAttribute('aria-expanded', 'false');
        controlledElement.setAttribute('aria-hidden', 'true');
        requestAnimationFrame(() => {
          currentTarget.classList.remove(styles.rotate);
          currentTarget.setAttribute('aria-expanded', 'false');
          controlledElement.style.setProperty('height', '0px');
          controlledElement.style.setProperty('opacity', '0');
        });
      } else {
        for (let i = 0; i < anchorTags.length; i += 1) {
          anchorTags[i].setAttribute('tabIndex', '0');
        }
        controlledElement.style.setProperty('height', '0px');
        controlledElement.style.setProperty('padding-top', '10px');
        controlledElement.style.setProperty('opacity', '0px');
        controlledElement.setAttribute('aria-expanded', 'true');
        controlledElement.setAttribute('aria-hidden', 'false');
        requestAnimationFrame(() => {
          currentTarget.setAttribute('aria-expanded', 'true');
          currentTarget.classList.add(styles.rotate);
          const height = controlledElement.scrollHeight;
          controlledElement.style.setProperty('height', `${height}px`);
          controlledElement.style.setProperty('opacity', '1');
          controlledElement.style.setProperty('padding-top', '0px');
        });
      }
    });
  };

  setMenuContainerNodeOverflow = () => {
    const menuContainerNodeHeight = this.menuContainerNode.scrollHeight;
    const globalMenuHeight = this.globalMenuNode.scrollHeight;
    const contextMenuHeight = this.contextMenuNode.scrollHeight;
    let contextMenuTranslateY =
      this.contextMenuNode.style.getPropertyValue('transform');
    contextMenuTranslateY = contextMenuTranslateY.replace(/[a-zA-z()]/g, '');
    contextMenuTranslateY = parseInt(contextMenuTranslateY, 10);
    const heightTakenByMenu = globalMenuHeight + contextMenuHeight;
    if (menuContainerNodeHeight < heightTakenByMenu + contextMenuTranslateY) {
      this.menuContainerNode.style.setProperty('overflow-y', 'auto');
    } else {
      this.menuContainerNode.style.removeProperty('overflow-y');
    }
  };

  handleGlobalMenuKeydown = (evt) => {
    switch (evt.keyCode || evt.which) {
      case 37:
        this.handleLeftArrowKeyForGlobalMenu(evt);
        break;
      case 39:
        this.handleRightArrowKeyForGlobalMenu(evt);
        break;
      default:
        break;
    }
  };

  handleContextMenuKeydown = (evt) => {
    switch (evt.keyCode || evt.which) {
      case 37:
        this.handleLeftArrowKeyForContextMenu(evt);
        break;
      case 39:
        this.handleRightArrowKeyForContextMenu(evt);
        break;
      default:
        break;
    }
  };

  handleMenuKeydown = (evt) => {
    switch (evt.keyCode || evt.which) {
      case 38:
        this.handleUpArrowKey(evt);
        break;
      case 40:
        this.handleDownArrowKey(evt);
        break;
      default:
        break;
    }
  };

  isNodeFocusable = (node) => {
    if (node.offsetHeight === 0 || node.getAttribute('disabled')) {
      return false;
    }
    if (
      node.tabIndex > -1 ||
      node.tagName.toLowerCase() === 'input' ||
      (node.tagName.toLowerCase() === 'a' &&
        node.getAttribute('href') !== '' &&
        node.getAttribute('href') !== null) ||
      (node.tagName.toLowerCase() === 'area' &&
        node.getAttribute('href') !== '' &&
        node.getAttribute('href') !== null) ||
      node.tagName.toLowerCase() === 'iframe'
    ) {
      return true;
    }
    return false;
  };

  findLastFocusableChild(rootNode) {
    for (let index = rootNode.children.length - 1; index >= 0; index -= 1) {
      const elm = rootNode.children[index];
      if (this.isNodeFocusable(elm)) return elm;
      if (elm.offsetHeight > 0 && elm.children.length > 0) {
        const temp = this.findLastFocusableChild(elm);
        if (temp !== null) return temp;
      }
    }
    return null;
  }

  findFirstFocusableChild(rootNode) {
    for (let index = 0; index < rootNode.children.length; index += 1) {
      const elm = rootNode.children[index];
      if (this.isNodeFocusable(elm)) return elm;
      if (elm.offsetHeight > 0 && elm.children.length > 0) {
        const temp = this.findFirstFocusableChild(elm);
        if (temp !== null) return temp;
      }
    }
    return null;
  }

  findNextFocusableElement(rootNode, target) {
    if (rootNode === target) return null;
    let targetNextSibling = target;
    while (
      targetNextSibling.nextElementSibling !== null &&
      targetNextSibling.nextElementSibling.offsetHeight > 0
    ) {
      targetNextSibling = targetNextSibling.nextElementSibling;
      if (this.isNodeFocusable(targetNextSibling)) return targetNextSibling;
      if (
        targetNextSibling.children.length > 0 &&
        targetNextSibling.offsetHeight > 0
      ) {
        const temp = this.findFirstFocusableChild(targetNextSibling);
        if (temp !== null) return temp;
      }
    }
    return this.findNextFocusableElement(rootNode, target.parentNode);
  }

  findPreviousFocusableElement(rootNode, target) {
    if (rootNode === target) return null;
    let targetPreviousSibling = target;
    while (
      targetPreviousSibling.previousElementSibling !== null &&
      targetPreviousSibling.previousElementSibling.offsetHeight > 0
    ) {
      targetPreviousSibling = targetPreviousSibling.previousElementSibling;
      if (this.isNodeFocusable(targetPreviousSibling)) { return targetPreviousSibling; }
      if (
        targetPreviousSibling.children.length > 0 &&
        targetPreviousSibling.offsetHeight > 0
      ) {
        const temp = this.findLastFocusableChild(targetPreviousSibling);
        if (temp !== null) return temp;
      }
    }
    return this.findPreviousFocusableElement(rootNode, target.parentNode);
  }

  handleUpArrowKey = ({ target }) => {
    const focusableItem = this.findPreviousFocusableElement(
      this.menuContainerNode,
      target,
    );
    if (focusableItem === null) {
      const lastFocusableItem = this.findLastFocusableChild(
        this.menuContainerNode,
      );
      if (lastFocusableItem !== null) lastFocusableItem.focus();
    } else {
      focusableItem.focus();
    }
  };

  handleLeftArrowKeyForContextMenu = ({ target }) => {
    this.contextMenuNode.classList.add(styles.animate);
    if (target.hasAttribute('aria-controls')) {
      const controlledElementIds = target.getAttribute('aria-controls');
      controlledElementIds.split(',').forEach((controlledElementId) => {
        const controlledElement = this.contextMenuNode.querySelector(
          `#${controlledElementId}`,
        );
        const ariaHidden = controlledElement.getAttribute('aria-hidden');
        if (ariaHidden === 'false') {
          let temp = controlledElement.parentNode;
          while (temp !== this.contextMenuNode) {
            if (
              temp.style.getPropertyValue('height') !== '' &&
              temp.style.getPropertyValue('height') !== 'auto'
            ) {
              temp.style.setProperty('height', 'auto');
            }
            temp = temp.parentNode;
          }
          target.classList.remove(styles.rotate);
          const { height } = controlledElement.getBoundingClientRect();
          controlledElement.setAttribute(
            'style',
            `height: ${height + 8}px; opacity: 1;`,
          );
          controlledElement.setAttribute('aria-expanded', 'false');
          controlledElement.setAttribute('aria-hidden', 'true');
          requestAnimationFrame(() => {
            controlledElement.setAttribute(
              'style',
              'height: 0px; padding-top: 0px; opacity: 0;',
            );
          });
        }
      });
    } else {
      let targetParentNode = target.parentNode;
      while (
        targetParentNode !== this.contextMenuNode &&
        targetParentNode.hasAttribute('aria-expanded') === false
      ) {
        targetParentNode = targetParentNode.parentNode;
      }
      targetParentNode.previousElementSibling.focus();
    }
  };

  handleRightArrowKeyForContextMenu = ({ target }) => {
    this.contextMenuNode.classList.add(styles.animate);
    if (target.hasAttribute('aria-controls')) {
      const controlledElementIds = target.getAttribute('aria-controls');
      controlledElementIds.split(',').forEach((controlledElementId) => {
        const controlledElement = this.contextMenuNode.querySelector(
          `#${controlledElementId}`,
        );
        if (controlledElement.getAttribute('aria-expanded') === 'true') {
          this.findFirstFocusableChild(controlledElement).focus();
        } else {
          let temp = controlledElement.parentNode;
          while (temp !== this.contextMenuNode) {
            if (
              temp.style.getPropertyValue('height') !== '' &&
              temp.style.getPropertyValue('height') !== 'auto'
            ) {
              temp.style.setProperty('height', 'auto');
            }
            temp = temp.parentNode;
          }
          target.classList.add(styles.rotate);
          controlledElement.setAttribute('style', 'height: auto;');
          const { height } = controlledElement.getBoundingClientRect();
          controlledElement.setAttribute(
            'style',
            'height: 0px; padding-top: 0px; opacity: 0;',
          );
          requestAnimationFrame(() => {
            controlledElement.setAttribute(
              'style',
              `height: ${height + 8}px; opacity: 1;`,
            );
          });
          controlledElement.setAttribute('aria-expanded', 'true');
          controlledElement.setAttribute('aria-hidden', 'false');
        }
      });
    }
  };

  handleLeftArrowKeyForGlobalMenu = ({ target }) => {
    if (target.hasAttribute('aria-controls')) {
      if (this.globalMenuNode.classList.contains(styles.isOpen) === true) {
        this.isGlobalMenuOpen = false;
      }
    } else {
      this.globalMenuNode.querySelector('button').focus();
    }
  };

  handleRightArrowKeyForGlobalMenu = ({ target }) => {
    if (target.hasAttribute('aria-controls')) {
      if (this.globalMenuNode.classList.contains(styles.isOpen) === false) {
        this.isGlobalMenuOpen = true;
      } else {
        const globalMenuList = this.globalMenuNode.querySelector('ol');
        const focusableItem = this.findFirstFocusableChild(globalMenuList);
        focusableItem.focus();
      }
    }
  };

  handleDownArrowKey = ({ target }) => {
    const focusableItem = this.findNextFocusableElement(
      this.menuContainerNode,
      target,
    );
    if (focusableItem === null) {
      const firstFocusableItem = this.findFirstFocusableChild(
        this.menuContainerNode,
      );
      if (firstFocusableItem !== null) firstFocusableItem.focus();
    } else {
      focusableItem.focus();
    }
  };

  render(props) {
    this.props = { ...this.defaultProps, ...props };
    this.controlledElementMap = [];
    const template = placeholderTemplate();
    const templateFrag = document
      .createRange()
      .createContextualFragment(template);
    this.globalMenuNode = templateFrag.querySelector(
      globalMenuPlaceholderSelector,
    );
    this.contextMenuNode = templateFrag.querySelector(
      contextMenuPlaceholderSelector,
    );
    this.greetingNode = templateFrag.querySelector(greetingPlaceholderSelector);
    emptyElement(this.menuContainerNode);
    this.menuContainerNode.appendChild(templateFrag);
    this.globalMenu = this.props.globalMenu;
    this.contextMenu = this.props.contextMenu;
    this.greeting = this.props.greeting;
    this.isGlobalMenuOpen = this.props.isGlobalMenuOpen;
    this.isEclientLink = this.props.isEclientLink;
    this.menuContainerNode.addEventListener('keydown', this.handleMenuKeydown);
    this.menuContainerNode.classList.add(styles.menuContainer);
  }
}

export default Menu;
